import { ComponentProps, useEffect, useState } from 'react';
import DeleteOutlined from '@ant-design/icons/lib/icons/DeleteOutlined';
import DownloadOutlined from '@ant-design/icons/lib/icons/DownloadOutlined';
import UploadOutlined from '@ant-design/icons/lib/icons/UploadOutlined';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { OpUpload } from 'new-components/DLS/OpUpload/OpUpload';
import { OpTypography } from 'new-components/DLS/OpTypography/OpTypography';
import { tableStateKeys } from 'new-components/DLS/OpTableCore/constants/tableStateKeys';
import { OpTableRawColumnType } from 'new-components/DLS/OpTableCore/types';
import { OpTable } from 'new-components/DLS/OpTable/OpTable';
import { OpCollapse } from 'new-components/DLS/OpCollapse/OpCollapse';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpPage } from 'new-components/OpPage/OpPage';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';

import { DATE_TIME_FORMAT } from '../../constants/dates';
import { toUTCByTimezone } from '../../utils/dates';
import STATUS from '../../constants/status';
import {
  fetchInvitationConfig,
  VisitorInvitationState,
} from '../../store/slices/visitorInvitationSlice';
import {
  fetchLocations,
  LocationsState,
} from '../../store/slices/locationsSlice';
import {
  fetchVisits,
  setVisitsEndDate,
  setVisitsStartDate,
} from '../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../store/store';
import {
  ConfigItem,
  InvitationConfig,
} from '../../types/visitorInvitationTypes';
import { User } from '../../types/userTypes';
import { VisitorType } from '../../types/VisitorTypeTypes';
import { formatFullName } from '../../utils/utils';

const SAMPLE_CSV_CONTENT = `Location,First Name,Last Name,Email,Visit Phone,Visitor Type,Start Time,End Time,Visit Company,Host,Visit Purpose,Visit Note
Enterprise Marketing Solutions,John,Doe,John.Doe@test.com,+1555551234,Visitor,2025-08-22 9:00 AM,2025-08-22 10:30 AM,Candy Corp,Anne Smith,Contract proposal review,Needs meeting room with projector access`;

const { Title } = OpTypography;

type UploadList = NonNullable<ComponentProps<typeof OpUpload>['fileList']>;

type BulkScheduleCsvRow = Record<string, string>;

interface ExcludedHost {
  userId: number;
}

const BulkSchedule = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { isValid: hasVisitorsBulkWrite } = useValidateScopes([
    'o{orgId}-visitorVisitorsBulk:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const [fileList, setFileList] = useState<UploadList>([]);
  const [uploadStatus, setUploadStatus] = useState<JSX.Element[] | null>(null);
  const [validEntries, setValidEntries] = useState<BulkScheduleCsvRow[]>([]);

  const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
  const [hosts, setHosts] = useState<User[]>([]);
  const [sampleCsvUrl, setSampleCsvUrl] = useState<string>('');

  const confirmModal = useConfirmModal();

  // Create blob URL for sample CSV
  useEffect(() => {
    const blob = new Blob([SAMPLE_CSV_CONTENT], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    setSampleCsvUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!orgId) return;

      try {
        if (!invitationConfig) dispatch(fetchInvitationConfig({ orgId }));
        if (locations.length === 0) {
          dispatch(fetchLocations({ orgId, status: 1 }));
        }

        const [usersResponse, excludedHostsResponse] = await Promise.all([
          heliumReceive('listUsers', [orgId]),
          heliumReceive('listAllExcludeHosts', [orgId]),
        ]);
        const usersData = Array.isArray(usersResponse?.data)
          ? (usersResponse.data as User[])
          : [];
        const excludedHostsData = Array.isArray(excludedHostsResponse?.data)
          ? (excludedHostsResponse.data as ExcludedHost[])
          : [];

        const activeUsers = usersData.filter(
          (user) => String(user.status) === 'A',
        );
        const excludedIds = excludedHostsData.map(
          (excludedHost) => excludedHost.userId,
        );

        const filteredHosts = activeUsers.filter(
          (host) => !excludedIds.includes(host.id),
        );
        setHosts(filteredHosts);

        const visitorTypesResponse = await heliumReceive('getVisitorTypes', [
          orgId,
        ]);
        setVisitorTypes(
          Array.isArray(visitorTypesResponse?.data)
            ? (visitorTypesResponse.data as VisitorType[])
            : [],
        );
      } catch {
        // Failed to fetch initial data
      }
    };
    fetchData();
  }, [dispatch, orgId, invitationConfig, locations.length]);

  const columns: OpTableRawColumnType<{
    field: string;
    required: string;
    acceptable: string;
    description: string;
  }>[] = [
    {
      label: t('bulkSchedule.fields.fieldName'),
      dataIndex: 'field',
      key: 'field',
    },
    {
      label: t('bulkSchedule.fields.required'),
      dataIndex: 'required',
      key: 'required',
    },
    {
      label: t('bulkSchedule.fields.acceptableValues'),
      dataIndex: 'acceptable',
      key: 'acceptable',
    },
    {
      label: t('bulkSchedule.fields.description'),
      dataIndex: 'description',
      key: 'description',
    },
  ];

  // Map: field key -> label & example
  const FIELD_INFO: Record<
    string,
    {
      label: string;
      example: string;
      description: string;
      configKey?: keyof InvitationConfig;
    }
  > = {
    Location: {
      label: t('bulkSchedule.fieldInfo.location.label'),
      example: t('bulkSchedule.fieldInfo.location.example'),
      description: t('bulkSchedule.fieldInfo.location.description'),
    },
    'First Name': {
      label: t('bulkSchedule.fieldInfo.firstName.label'),
      example: t('bulkSchedule.fieldInfo.firstName.example'),
      description: t('bulkSchedule.fieldInfo.firstName.description'),
      configKey: 'firstName',
    },
    'Last Name': {
      label: t('bulkSchedule.fieldInfo.lastName.label'),
      example: t('bulkSchedule.fieldInfo.lastName.example'),
      description: t('bulkSchedule.fieldInfo.lastName.description'),
      configKey: 'lastName',
    },
    Email: {
      label: t('bulkSchedule.fieldInfo.email.label'),
      example: t('bulkSchedule.fieldInfo.email.example'),
      description: t('bulkSchedule.fieldInfo.email.description'),
      configKey: 'email',
    },
    'Visit Phone': {
      label: t('bulkSchedule.fieldInfo.visitPhone.label'),
      example: t('bulkSchedule.fieldInfo.visitPhone.example'),
      description: t('bulkSchedule.fieldInfo.visitPhone.description'),
      configKey: 'visitPhone',
    },
    'Visitor Type': {
      label: t('bulkSchedule.fieldInfo.visitorType.label'),
      example: t('bulkSchedule.fieldInfo.visitorType.example'),
      description: t('bulkSchedule.fieldInfo.visitorType.description'),
    },
    'Start Time': {
      label: t('bulkSchedule.fieldInfo.startTime.label'),
      example: t('bulkSchedule.fieldInfo.startTime.example'),
      description: t('bulkSchedule.fieldInfo.startTime.description'),
    },
    'End Time': {
      label: t('bulkSchedule.fieldInfo.endTime.label'),
      example: t('bulkSchedule.fieldInfo.endTime.example'),
      description: t('bulkSchedule.fieldInfo.endTime.description'),
    },
    'Visit Company': {
      label: t('bulkSchedule.fieldInfo.visitCompany.label'),
      example: t('bulkSchedule.fieldInfo.visitCompany.example'),
      description: t('bulkSchedule.fieldInfo.visitCompany.description'),
      configKey: 'visitCompany',
    },
    Host: {
      label: t('bulkSchedule.fieldInfo.host.label'),
      example: t('bulkSchedule.fieldInfo.host.example'),
      description: t('bulkSchedule.fieldInfo.host.description'),
      configKey: 'host',
    },
    'Visit Purpose': {
      label: t('bulkSchedule.fieldInfo.visitPurpose.label'),
      example: t('bulkSchedule.fieldInfo.visitPurpose.example'),
      description: t('bulkSchedule.fieldInfo.visitPurpose.description'),
      configKey: 'visitPurpose',
    },
    'Visit Note': {
      label: t('bulkSchedule.fieldInfo.visitNote.label'),
      example: t('bulkSchedule.fieldInfo.visitNote.example'),
      description: t('bulkSchedule.fieldInfo.visitNote.description'),
      configKey: 'visitNotes',
    },
  };

  const ALWAYS_INCLUDED = [
    'Location',
    'Visitor Type',
    'Start Time',
    'End Time',
  ];

  const parseFlexibleDate = (value: string): dayjs.Dayjs | null => {
    if (!value) return null;

    const cleaned = value.trim().replace(/\u00A0/g, '');
    const formats = [
      'YYYY-MM-DD h:mm A',
      'YYYY-MM-DD hh:mm A',
      'YYYY-MM-DD HH:mm:ss',
      'YYYY-MM-DD h:mm',
      'YYYY-MM-DD HH:mm',
      'MM/DD/YY h:mm A',
      'MM/DD/YYYY h:mm A',
      'MM/DD/YY hh:mm A',
      'MM/DD/YYYY hh:mm A',
      'MM/DD/YYYY h:mm',
      'MM/DD/YYYY HH:mm',
      'M/D/YY H:mm',
      'M/D/YY h:mm A',
      'M/D/YYYY H:mm',
      'M/D/YYYY h:mm A',
      'MM/DD/YY H:mm',
      'MM/DD/YYYY H:mm',
    ];

    for (const fmt of formats) {
      const parsed = dayjs(cleaned, fmt, true);
      if (parsed.isValid()) return parsed;
    }
    return null;
  };

  const getIncludedFields = (
    config: InvitationConfig | null | undefined,
  ): string[] => {
    if (!config) return ALWAYS_INCLUDED;

    return Object.entries(FIELD_INFO)
      .filter(
        ([key, info]) =>
          ALWAYS_INCLUDED.includes(key) ||
          (info.configKey &&
            (config[info.configKey] as ConfigItem)?.included === 1),
      )
      .map(([key]) => key);
  };

  const INCLUDED_HEADERS = getIncludedFields(invitationConfig);

  const data = Object.entries(FIELD_INFO).map(([field, info]) => {
    const isAlwaysRequired = ALWAYS_INCLUDED.includes(field);
    const isRequired =
      isAlwaysRequired ||
      (info.configKey &&
        invitationConfig &&
        (invitationConfig[info.configKey] as ConfigItem)?.required === 1);

    return {
      field: info.label,
      required: isRequired ? t('Yes') : t('No'),
      acceptable: `${t('E.g.')} ${info.example}`,
      description: info.description,
    };
  });

  const handleUpload = async () => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;

      const parsed = Papa.parse<BulkScheduleCsvRow>(text, { header: true });
      const actualHeaders = parsed.meta.fields || [];

      const missingHeaders = INCLUDED_HEADERS.filter(
        (h) => !actualHeaders.includes(h),
      );

      if (missingHeaders.length > 0) {
        const statusLines: JSX.Element[] = [];

        statusLines.push(
          <div>
            <strong>{t('bulkSchedule.uploadErrors.invalidFormat')}</strong>
          </div>,
        );

        if (missingHeaders.length > 0) {
          statusLines.push(
            <div style={{ color: 'red', marginTop: 8 }}>
              {t('bulkSchedule.uploadErrors.missingHeaders', {
                headers: missingHeaders.join(', '),
              })}
            </div>,
          );
        }
        setUploadStatus(statusLines);
        return;
      }
      const seenKeys = new Set<string>();
      const uniqueRows = parsed.data.filter((row) => {
        const start = parseFlexibleDate(row['Start Time'] ?? '')?.format(
          'YYYY-MM-DD HH:mm',
        );
        const end = parseFlexibleDate(row['End Time'] ?? '')?.format(
          'YYYY-MM-DD HH:mm',
        );
        const key = [
          row['First Name']?.trim().toLowerCase(),
          row['Last Name']?.trim().toLowerCase(),
          row.Email?.trim().toLowerCase(),
          start,
          end,
        ].join('|');

        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

      const deduplicatedCsv = Papa.unparse(uniqueRows);

      const {
        errors,
        validEntries: validatedEntries,
        totalEntries,
      } = validateCsv(deduplicatedCsv, invitationConfig!, fileList[0].name);
      setValidEntries(validatedEntries);

      const statusLines: JSX.Element[] = [];
      if (errors.length > 0) {
        statusLines.push(
          <div>
            {t('bulkSchedule.uploadErrors.successfulUpload', {
              valid: validatedEntries.length,
              total: totalEntries,
            })}
          </div>,
        );
        statusLines.push(<br />);
        statusLines.push(
          <strong>{t('bulkSchedule.uploadErrors.errorsTitle')}</strong>,
        );

        errors.forEach((err, i) => {
          statusLines.push(
            <div key={i} style={{ margin: '8px 0' }}>
              {err.split('\n').map((line, idx) =>
                idx === 0 ? (
                  <div key={idx}>{line}</div>
                ) : (
                  <div key={idx} style={{ color: 'red' }}>
                    {line.trim()}
                  </div>
                ),
              )}
            </div>,
          );
        });

        statusLines.push(
          <div style={{ marginTop: 12 }}>
            {t('bulkSchedule.uploadErrors.importInstruction', {
              count: validatedEntries.length,
            })}
          </div>,
        );
      } else {
        statusLines.push(
          <div>
            {t('bulkSchedule.uploadErrors.successfulUpload', {
              valid: validatedEntries.length,
              total: totalEntries,
            })}
          </div>,
        );
      }

      setUploadStatus(statusLines);
    };
    const fileObject = fileList[0]?.originFileObj;
    if (!fileObject) {
      return;
    }

    reader.readAsText(fileObject);
    notificationService.show(
      'success',
      t('bulkSchedule.uploadErrors.scheduleSuccess'),
    );
  };

  const validateCsv = (
    csvText: string,
    config: InvitationConfig,
    fileName: string,
  ) => {
    const results = Papa.parse<BulkScheduleCsvRow>(csvText, {
      header: true,
    });
    const errors: string[] = [];
    const validItems: BulkScheduleCsvRow[] = [];

    const hostNames = hosts.map((host) =>
      formatFullName(
        host.identity.firstName || '',
        host.identity.middleName || '',
        host.identity.lastName || '',
      ),
    );

    const isRequired = (field: keyof InvitationConfig) => {
      const fieldConfig = config[field];
      if (
        typeof fieldConfig === 'object' &&
        fieldConfig !== null &&
        'included' in fieldConfig &&
        'required' in fieldConfig
      ) {
        return fieldConfig.included === 1 && fieldConfig.required === 1;
      }
      return false;
    };

    const rows = results.data.filter((row) =>
      Object.values(row).some((cell) => {
        return typeof cell === 'string' && cell.trim() !== '';
      }),
    );

    rows.forEach((row) => {
      const rowErrors: string[] = [];

      const fullName =
        formatFullName(row['First Name'] || '', '', row['Last Name'] || '') ||
        '(Unknown)';

      const isEmpty = (value: string | null | undefined) =>
        !value || value.trim() === '';

      if (isEmpty(row.Location)) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingLocation'));
      } else if (
        !locations
          .map((loc) => loc.name.toLowerCase())
          .includes(row.Location.trim().toLowerCase())
      ) {
        rowErrors.push(
          t('bulkSchedule.rowErrors.invalidLocation', {
            location: row.Location,
          }),
        );
      }

      if (isEmpty(row['First Name'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingFirstName'));
      }
      if (isEmpty(row['Last Name'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingLastName'));
      }
      if (isRequired('visitPhone') && isEmpty(row['Visit Phone'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingVisitPhone'));
      }

      if (isRequired('email') && isEmpty(row.Email)) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingEmail'));
      }
      if (row.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.Email.trim())) {
        rowErrors.push(t('bulkSchedule.rowErrors.invalidEmail'));
      }

      if (isEmpty(row['Visitor Type'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingVisitorType'));
      } else if (
        !visitorTypes
          .map((vt) => vt.name?.toLowerCase())
          .includes(row['Visitor Type'].trim().toLowerCase())
      ) {
        rowErrors.push(
          t('bulkSchedule.rowErrors.invalidVisitorType', {
            visitorType: row['Visitor Type'],
          }),
        );
      }

      const startTimeStr = row['Start Time']?.trim();
      const endTimeStr = row['End Time']?.trim();

      const startTime = parseFlexibleDate(startTimeStr);
      const endTime = parseFlexibleDate(endTimeStr);

      if (!startTime) {
        rowErrors.push(t('bulkSchedule.rowErrors.invalidStartTime'));
      } else if (dayjs(startTime).isBefore(dayjs())) {
        rowErrors.push(t('bulkSchedule.rowErrors.startTimePast'));
      }

      if (!endTime) rowErrors.push(t('bulkSchedule.rowErrors.invalidEndTime'));
      else if (startTime && !startTime.isBefore(endTime)) {
        rowErrors.push(t('bulkSchedule.rowErrors.startTimeBeforeEndTime'));
      }

      if (isRequired('host') && isEmpty(row.Host)) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingHost'));
      }
      if (
        row.Host &&
        !hostNames
          .map((h) => h.toLowerCase())
          .includes(row.Host.trim().toLowerCase())
      ) {
        rowErrors.push(
          t('bulkSchedule.rowErrors.invalidHost', { host: row.Host }),
        );
      }

      if (isRequired('visitCompany') && isEmpty(row['Visit Company'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingCompany'));
      }
      if (isRequired('visitPurpose') && isEmpty(row['Visit Purpose'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingPurpose'));
      }
      if (isRequired('visitNotes') && isEmpty(row['Visit Note'])) {
        rowErrors.push(t('bulkSchedule.rowErrors.missingNote'));
      }

      if (rowErrors.length > 0) {
        errors.push(
          t('bulkSchedule.errorSummary', { fullName, fileName }) +
            '\n' +
            `  ${rowErrors.join(', ')}`,
        );
      } else {
        validItems.push(row);
      }
    });

    return { errors, validEntries: validItems, totalEntries: rows.length };
  };

  const handleImport = async () => {
    try {
      const invitationPayloads = validEntries.map((row) => {
        const location = locations.find(
          (loc) =>
            loc.name.toLowerCase() ===
            (row.Location ?? '').trim().toLowerCase(),
        );
        const tz = location?.timezone;

        const start = toUTCByTimezone(
          parseFlexibleDate(row['Start Time'] ?? ''),
          tz,
        )?.format(DATE_TIME_FORMAT);
        const end = toUTCByTimezone(
          parseFlexibleDate(row['End Time'] ?? ''),
          tz,
        )?.format(DATE_TIME_FORMAT);

        const host = hosts.find(
          (h) =>
            formatFullName(
              h.identity.firstName || '',
              h.identity.middleName || '',
              h.identity.lastName || '',
            )
              .toLowerCase()
              .trim() === (row.Host ?? '').trim().toLowerCase(),
        );

        const visitorType = visitorTypes.find(
          (vt) =>
            vt.name?.toLowerCase() ===
            (row['Visitor Type'] ?? '').trim().toLowerCase(),
        );

        return {
          locationId: location?.id,
          visitorTypeId: visitorType?.id,
          company:
            invitationConfig?.visitCompany.included === 1
              ? row['Visit Company']?.trim() || null
              : null,
          scheduleIn: start,
          scheduleOut: end,
          hostId: invitationConfig?.host.included === 1 ? host?.id : null,
          purpose:
            invitationConfig?.visitPurpose.included === 1
              ? row['Visit Purpose']?.trim() || null
              : null,
          note:
            invitationConfig?.visitNotes.included === 1
              ? row['Visit Note']?.trim() || null
              : null,
          visitors: [
            {
              firstName:
                invitationConfig?.firstName.included === 1
                  ? row['First Name']?.trim() || null
                  : null,
              middleName: null,
              lastName:
                invitationConfig?.lastName.included === 1
                  ? row['Last Name']?.trim() || null
                  : null,
              email:
                invitationConfig?.email.included === 1
                  ? row.Email?.trim() || null
                  : null,
              mobilePhone:
                invitationConfig?.visitPhone.included === 1
                  ? row['Visit Phone']?.trim() || null
                  : null,
              userId: null,
              status: STATUS.PENDING.id,
              signIn: null,
              signOut: null,
            },
          ],
        };
      });

      await Promise.all(
        invitationPayloads.map((payload) =>
          heliumSend('newInvitation', [orgId], payload),
        ),
      );

      notificationService.show(
        'success',
        t('bulkSchedule.uploadErrors.scheduleSuccess'),
      );

      dispatch(
        setVisitsStartDate(dayjs().startOf('day').format(DATE_TIME_FORMAT)),
      );
      dispatch(setVisitsEndDate(dayjs().endOf('day').format(DATE_TIME_FORMAT)));
      dispatch(fetchVisits({ orgId }));

      navigate(`/o/${orgId}/visitors`);
    } catch {
      notificationService.show(
        'error',
        t('bulkSchedule.uploadErrors.scheduleError'),
      );
    }
  };

  return (
    <OpPage title={t('bulkSchedule.title')} tooltip={t('bulkSchedule.tooltip')}>
      <OpTypography.Paragraph>
        {t('bulkSchedule.description1')}
      </OpTypography.Paragraph>
      <OpTypography.Paragraph>
        {t('bulkSchedule.description2')}
      </OpTypography.Paragraph>

      <OpCollapse
        defaultActiveKey={['1']}
        items={[
          {
            key: '1',
            label: t('bulkSchedule.validFields'),
            children: (
              <OpTable
                uiStateKey={
                  tableStateKeys.UsersImportPageValidFieldsLegacyTable
                }
                columns={columns}
                dataSource={data}
                pagination={false}
                rowKey="field"
                scroll={{ x: 'max-content' }}
                allowGlobalSearch={false}
                allowShowHideColumns={false}
                allowExport={false}
              />
            ),
          },
        ]}
      />

      {hasVisitorsBulkWrite ? (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <OpUpload
              beforeUpload={() => false}
              onChange={({ fileList: nextFileList }) =>
                setFileList(nextFileList)
              }
              showUploadList={false}
              fileList={fileList}
              accept=".csv"
              maxCount={1}
            >
              <OpButton icon={<UploadOutlined />}>
                {t('bulkSchedule.selectCsv')}
              </OpButton>
            </OpUpload>

            {fileList.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'var(--colorInfoBgHover)',
                  padding: '0px 8px',
                  borderRadius: 4,
                }}
              >
                <span>{fileList[0].name}</span>
                <OpButton
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setFileList([]);
                    setUploadStatus(null);
                  }}
                  style={{ marginLeft: 8 }}
                />
              </div>
            )}
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <OpButton
              type="primary"
              onClick={() => {
                confirmModal({
                  title: t('bulkSchedule.uploadConfirmationTitle'),
                  content: t('bulkSchedule.uploadConfirmationContent'),
                  okText: t('bulkSchedule.ok'),
                  cancelText: t('bulkSchedule.cancel'),
                  onOk: handleUpload,
                });
              }}
              disabled={!fileList.length}
            >
              {t('bulkSchedule.selectCsv')}
            </OpButton>

            <OpButton
              icon={<DownloadOutlined />}
              href={sampleCsvUrl}
              download="bulk-schedule-sample.csv"
            >
              {t('bulkSchedule.downloadSampleCsv')}
            </OpButton>
          </div>

          <div style={{ marginTop: 24 }}>
            <Title level={4}>{t('bulkSchedule.uploadStatusTitle')}</Title>
            <div
              style={{
                minHeight: 200,
                background: 'var(--colorInfoBg)',
                padding: 16,
                border: '1px solid var(--colorBorder)',
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                height: 300,
                overflowY: 'auto',
              }}
            >
              {uploadStatus || t('bulkSchedule.noFileUploaded')}
            </div>
          </div>

          <div style={{ marginTop: 24 }}>
            <OpButton
              type="primary"
              onClick={() => {
                confirmModal({
                  title: t('bulkSchedule.importConfirmationTitle'),
                  content: t('bulkSchedule.importConfirmationContent'),
                  okText: t('bulkSchedule.import'),
                  cancelText: t('bulkSchedule.cancel'),
                  onOk: handleImport,
                });
              }}
              disabled={!uploadStatus}
            >
              {t('bulkSchedule.import')}
            </OpButton>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          <OpButton
            icon={<DownloadOutlined />}
            href={sampleCsvUrl}
            download="bulk-schedule-sample.csv"
          >
            {t('bulkSchedule.downloadSampleCsv')}
          </OpButton>
        </div>
      )}
    </OpPage>
  );
};

export default BulkSchedule;
